import { useState } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Search,
  FileText,
  Printer,
  ArrowRight,
  Calendar,
  Building2,
  Package,
} from "lucide-react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { formatCurrency } from "@/lib/billingUtils";

interface DeliveryNote {
  id: string;
  delivery_note_number: string;
  date: string;
  customer_name: string;
  customer_id?: string;
  work_order_id?: string;
  work_order_number?: string;
  status: 'draft' | 'issued' | 'invoiced';
  total_amount: number;
  notes?: string;
  created_at: string;
}

const statusColors: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700",
  issued: "bg-blue-100 text-blue-700",
  invoiced: "bg-green-100 text-green-700",
};

const statusLabels: Record<string, string> = {
  draft: "Borrador",
  issued: "Emitido",
  invoiced: "Facturado",
};

export default function DeliveryNotes() {
  const { userRole } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Por ahora, mostrar mensaje de que el módulo está en desarrollo
  // Los albaranes actuales se generan desde órdenes de trabajo

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Albaranes</h1>
            <p className="text-muted-foreground">
              Gestión de albaranes de entrega
            </p>
          </div>
          <Button disabled>
            <Plus className="w-4 h-4 mr-2" />
            Nuevo albarán
          </Button>
        </div>

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <FileText className="w-5 h-5" />
              Albaranes desde Órdenes de Trabajo
            </CardTitle>
            <CardDescription className="text-blue-700">
              Actualmente los albaranes se generan automáticamente desde las órdenes de trabajo completadas.
              Puedes acceder a ellos desde el detalle de cada orden de trabajo.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Button variant="outline" asChild>
                <Link to="/work-orders">
                  <Package className="w-4 h-4 mr-2" />
                  Ver Órdenes de Trabajo
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/billing/invoices">
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Crear Factura
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por número, cliente..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="draft">Borrador</SelectItem>
                  <SelectItem value="issued">Emitido</SelectItem>
                  <SelectItem value="invoiced">Facturado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Empty State */}
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">
                Módulo de Albaranes Independiente
              </h3>
              <p className="text-muted-foreground max-w-md mx-auto mb-6">
                Esta sección permitirá crear albaranes independientes de las órdenes de trabajo.
                Por ahora, genera albaranes desde las OTs completadas.
              </p>
              <div className="flex justify-center gap-4">
                <Button variant="outline" asChild>
                  <Link to="/work-orders">
                    Ver Órdenes de Trabajo
                  </Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Future: Table of delivery notes */}
        {/*
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Número</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>OT</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              ...
            </TableBody>
          </Table>
        </Card>
        */}
      </div>
    </DashboardLayout>
  );
}
